import $ from 'jquery';
import get from 'lodash/get';
import _ from 'underscore';

import RetailManager from 'app/retail_manager/RetailManager';
import BaseObject from 'app/src/base/BaseObject';
import Decimal from 'app/src/data/base/Decimal';
import { Eventbus } from 'app/src/data/Eventbus';
import Format from 'app/src/data/Format';
import CreditAccount from 'app/src/data/models/CreditAccount';
import Customer from 'app/src/data/models/Customer';
import PaymentModel from 'app/src/data/models/Payment';
import PaymentCollection from 'app/src/data/models/PaymentCollection';
import PaymentType from 'app/src/data/models/PaymentType';
import Sale from 'app/src/data/models/Sale';
import SaleLine from 'app/src/data/models/SaleLine';
import SalePayment from 'app/src/data/models/SalePayment';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import Filter from 'app/src/data/models/utils/Filter';
import Localize from 'app/src/localize/Localize';
import CashPayment from 'app/src/payments/CashPayment';
import DepositManager from 'app/src/payments/DepositManager';
import DepositPayment from 'app/src/payments/DepositPayment';
import GiftCardPayment from 'app/src/payments/GiftCardPayment';
import Payment from 'app/src/payments/Payment';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentList from 'app/src/payments/PaymentList';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import Monitoring from 'shared_modules/Monitoring';

// events triggered by the SaleManager
const _Events = {
  PAYMENT_TOTAL_CHANGED: 'paymentsRecalculated',
  CALCTIPS_CHANGED: 'calcTipsChange',
  BALANCE_CHANGED: 'balanceChanged',
  CHANGE_DUE_CHANGED: 'changeDueChanged',
  CASH_CHANGED: 'cashChanged',
  DEPOSITS_CHANGED: 'depositsChanged',
  SALE_UPDATING: 'saleUpdating',
  SALE_REFRESHING: 'saleRefreshing',
  SALE_REFRESHED: 'saleRefreshed',
  COMPLETE_SALE_STARTED: 'completeSaleStarted',
  COMPLETE_SALE_FAILED: 'completeSaleFailed',
};

// used to identify errors to users of the SaleManager
const _Errors = {
  NO_ERROR: 1, // no error has occurred,
  USER_FACING: 2, // used to identify that the error that may be user facing
  INTERNAL: 500, // and internal error has occurred
};

const _GIFT_CARD_MODULE_NAME = 'gift_card';

/**
 * Class definition of a Sale Manager status.  Used to respond to users
 * of the Sale Manager to report errors.  If the error type is USER_FACING then
 * the message will be localized.
 */
class SaleManagerStatus {
  constructor(error = _Errors.NO_ERROR, msg = '') {
    this._error = error;
    this._msg = error === _Errors.USER_FACING ? Localize.translate(msg) : msg;
  }

  /**
   * getter: Retrieves the error code from the message.
   *
   * @returns {number}    The error code
   */
  get error() {
    return this._error;
  }

  /**
   * getter
   * @returns {string}    The localized string
   */
  get msg() {
    return this._msg;
  }
}

/**
 * Class definition of the sale manager.  This class is responsible for maintaining the sale state
 * and provides an interface to perform actions on the sale.  An instance of this class would be
 * instantiated when a sale is in progress.
 */
export default class SaleManager extends BaseObject {
  static get Events() {
    return _Events;
  }

  static get Errors() {
    return _Errors;
  }

  /**
   * Constructor
   *
   * @param {Sale} sale       The Sale model to associate with this manager instance
   * @param {PaymentCollection} payments  A collection of payment service payments associated with the sale
   * @param {Object} options  An object hash of the options passed to the Sale manger.  Possible
   *                          options are:
   * @param {Customer} options.attachedCustomer   The customer attached to the sale
   * @param {SalePaymentCollection} options.refundablePayments    A collection of refundable payments
   */
  constructor(sale, payments, options = {}) {
    super(options);

    if (!(sale instanceof Sale)) {
      throw 'A sale must be specified when constructing a SaleManager';
    }

    this._sale = sale;
    this._pendingPayments = {};
    this._servicePayments = payments || new PaymentCollection();
    this._isPaymentCollectionFromService = options.isPaymentCollectionFromService || false;
    this._isSaleUpdating = false;

    if (options.attachedCustomer && options.attachedCustomer instanceof Customer) {
      this._attachedCustomer = options.attachedCustomer;
    }

    this._giftCardModule = options.giftCardModule ? options.giftCardModule : false;

    this._depositManager = new DepositManager(this.sale);

    if (options.refundablePayments) {
      this.refundablePayments = options.refundablePayments;
    }

    this.listenTo(this._sale, 'change:displayableTotal', this._onTotalChanged);

    this._calculatePayments();
  }

  /**
   * Constructs a SaleManager using an existing sale ID.  Retrieves the model using the API
   * and injects the result into the construction of a new SaleManager.  The SaleManager instance
   * is returned with the resolved promise.
   *
   * @param {Number} saleID       The sale ID to continue
   * @returns {jQuery.Promise}    Resolves hen the a sale has been successfully retrieved.
   *                              Resolves with a new SaleManager instance.
   */
  static continueSale(saleID) {
    const deferred = $.Deferred();
    const servicePaymentDeferred = $.Deferred();
    const filter = new Filter().loadRelations('all');
    const servicePayments = new PaymentCollection();
    const paymentFilter = new Filter().exactly('saleID', saleID);
    const sale = new Sale({ saleID });
    const options = {};
    const supportMessage = `Try again and if the problem persists, contact <a class="strong-link" target="_blank" href="https://www.lightspeedhq.com/support/retail">Support</a>`;

    // We make an attempt to fetch the refundable payment list
    // from the pay-service
    servicePayments
      .fetch(paymentFilter)
      .done(() => {
        options.isPaymentCollectionFromService = true;
        servicePaymentDeferred.resolve();
      })
      .fail(error => {
        // TODO: Remove this message when the health check banner will be present in CLD-30509.
        if (error) {
          const { message } = error.responseJSON;
          window.alertUser(message, 300);
        }
        options.isPaymentCollectionFromService = false;
        servicePaymentDeferred.reject();
      });

    // Regardless of the API's availability to return the list
    // of refundable payment, we will still display the non-integrated
    // payments options. A merchant can still finish the sale using
    // cash or any other non-integrated payment method.
    $.when(servicePaymentDeferred).always(() => {
      sale
        .fetch(filter)
        .done(() => {
          let customer = null;
          let customerDeferred = $.Deferred();
          const refundPaymentsDeferred = $.Deferred();
          const giftCardModuleDeferred = $.Deferred();

          // Fetch Customer information
          if (sale.get('customerID') > 0) {
            customer = new Customer({ customerID: sale.get('customerID') });
            customerDeferred = customer.fetch(filter);
          } else {
            customerDeferred.resolve();
          }

          // Fetch Refundable Payments
          SaleManager.fetchRefundablePayments(sale)
            .done(refundablePayments => {
              refundPaymentsDeferred.resolve(refundablePayments);
            })
            .fail(fetchRefundablePaymentFailure => {
              const reason = get(fetchRefundablePaymentFailure, 'responseJSON.message', null);
              refundPaymentsDeferred.reject({
                message: `Unable to get any refundable payments from the payment-service`,
                reason,
              });
            });

          // Get gift card module
          window.merchantos.session
            .getModule(_GIFT_CARD_MODULE_NAME)
            .done(giftCardModule => {
              giftCardModuleDeferred.resolve(giftCardModule);
            })
            .fail(getModuleGiftCardFailure => {
              const reason = get(getModuleGiftCardFailure, 'responseJSON.message', null);

              giftCardModuleDeferred.reject({
                message: `Unable to use the gift card at the moment`,
                reason,
              });
            });

          $.when(refundPaymentsDeferred, giftCardModuleDeferred, customerDeferred)
            .fail(({ message, reason }) => {
              Monitoring.NRError(new Error(message), 'SaleManager', { saleID, reason });
            })
            .always((refundablePayments, giftCardModule) => {
              options.attachedCustomer = customer;
              options.refundablePayments = refundablePayments;
              options.giftCardModule = giftCardModule;
              deferred.resolve(new SaleManager(sale, servicePayments, options));
            });
        })
        .fail(salePaymentFailure => {
          const message = `Unable to get the sale information to initialize the Sale Manager`;
          const reason = get(salePaymentFailure, 'responseJSON.message', null);

          Monitoring.NRError(new Error(message), 'SaleManager', { saleID, reason });

          deferred.reject(
            `Payment processing capabilities are temporarily unavailable. ${supportMessage}`
          );
        });
    });

    return deferred.promise();
  }

  /**
   * Constructs a SaleManager by refunding an existing sale.  Retrieves the model using the API
   * and injects the result into the construction of a new SaleManager.  The SaleManager instance
   * is returned with the resolved promise.
   *
   * @param {Number} saleID       The sale ID to continue
   * @returns {jQuery.Promise}    Resolves when a refund sale has been successfully created.
   *                              Resolves with a new SaleManager instance.
   */
  static refundSale(saleID) {
    let filter = new Filter().loadRelations('all');
    let deferred = $.Deferred();
    let sale = new Sale({ saleID });

    sale
      .refund(filter)
      .done(() => {
        deferred.resolve(new SaleManager(sale));
      })
      .fail(() => {
        window.console.error('Could not create a SaleManager to refund the sale for:', saleID);
        deferred.reject();
      });

    return deferred.promise();
  }

  /**
   * Retrieves refundable payments from a prior sale.
   *
   * @param {Sale} sale   The sale to fetch the refundable payments
   * @returns {jQuery.Promise}  Resolves with a PaymentList when the refundable payments has been retrieved
   */
  static fetchRefundablePayments(sale) {
    let deferred = $.Deferred();

    if (sale && sale instanceof Sale) {
      let saleLines = sale.get('SaleLines');

      if (
        saleLines &&
        saleLines.SaleLine &&
        saleLines.SaleLine.length &&
        saleLines.SaleLine[0].parentSaleLineID > 0
      ) {
        // find a parent sale line so we can figure out which sale the payments are
        // attached to
        let parentSaleLine = new SaleLine({
          saleLineID: saleLines.SaleLine[0].parentSaleLineID,
        });

        parentSaleLine
          .fetch()
          .done(() => {
            // now fetch the payments associated with the original sale
            let filter = new Filter()
              .exactly('saleID', parentSaleLine.get('saleID'))
              .loadRelations('all');
            let refundableSalePayments = new SalePaymentCollection();
            let refundableServicePayments = new PaymentCollection();
            $.when(refundableSalePayments.fetch(filter), refundableServicePayments.fetch(filter))
              .done(() => {
                deferred.resolve(
                  PaymentList.fromPayments(refundableSalePayments, refundableServicePayments)
                );
              })
              .fail(errorFromPromises => {
                deferred.reject(errorFromPromises);
              });
          })
          .fail(errorFromFetching => {
            deferred.reject(errorFromFetching);
          });
      } else {
        deferred.resolve(new PaymentList());
      }
    } else {
      deferred.reject(`Cannot fetch refundable payments for the sale: ${sale}`);
    }

    return deferred.promise();
  }

  /**
   * Return if the sale is getting updated
   * by an external source (e.g.: Ajax call)
   */
  get isSaleUpdating() {
    return this._isSaleUpdating;
  }

  /**
   * Set this flag to determine if the sale is getting
   * updated by an external source (e.g.: Ajax call)
   */
  set isSaleUpdating(status) {
    if (typeof status !== 'boolean') {
      throw new Error(`_isSaleUpdating only accepts boolean you provided :: ${typeof status}`);
    }

    this._isSaleUpdating = status;
    return this._isSaleUpdating;
  }

  /**
   * Retrieves any pending credit/debit transactions in the sale.  This can happen if the
   * payment flow was interrupted in some fashion (e.g. browser was closed during a transaction).
   */
  get pendingCreditDebitTransaction() {
    const pendingTransaction = this._servicePayments.findWhere({
      status: PaymentModel.Status.PENDING,
    });

    return pendingTransaction;
  }

  /**
   * Getter for the Sale model
   *
   * @returns {Sale}  The Sale model managed by this instance of the SaleManager
   */
  get sale() {
    return this._sale;
  }

  /**
   * Getter for retrieving the deposit manager
   *
   * @returns {DepositManager}
   */
  get deposits() {
    return this._depositManager;
  }

  /**
   * Getter for retrieving whether or not we have the gift card module
   *
   * @returns {DepositManager}
   */
  get giftCardModule() {
    return this._giftCardModule;
  }

  /**
   * Getter for the Customer model
   *
   * @returns {Customer}  The customer associated with this sale managed  by
   *                      this instance of the SaleManager
   */
  get customer() {
    return this._attachedCustomer;
  }

  /**
   * Getter for the total including pending payments
   *
   * @returns {Decimal}
   */
  get pendingPaymentsTotal() {
    return this._totalPayments;
  }

  /**
   * Setter for the total including pending payments
   *
   * @param {*} total The new total including pending payments
   */
  set pendingPaymentsTotal(total) {
    this._totalPayments = new Decimal(total); // safe decimal precision
    this.trigger(_Events.PAYMENT_TOTAL_CHANGED, this._totalPayments);
  }

  /**
   * Getter for the calcTips
   *
   * @returns {Decimal} calcTips
   */
  get calcTips() {
    return this._calcTips;
  }

  /**
   * Setter for the calcTips
   *
   * @param {Decimal} calcTips The calcTips
   */
  set calcTips(calcTips) {
    this._calcTips = new Decimal(calcTips);
    this.trigger(_Events.CALCTIPS_CHANGED, this._calcTips);
  }

  /**
   * Getter for the balance including pending payments
   *
   * @returns {Decimal}
   */
  get pendingBalance() {
    return this._pendingBalance;
  }

  /**
   * Setter for the balance including pending payments
   *
   * @param {*} pendingBalance The new balance including pending payments
   */
  set pendingBalance(pendingBalance) {
    this._pendingBalance = new Decimal(pendingBalance); // safe decimal precision
    this.trigger(_Events.BALANCE_CHANGED, this._pendingBalance);
  }

  /**
   * Getter for the change due including pending payments
   *
   * @returns {Decimal}
   */
  get pendingChange() {
    return this._pendingChange;
  }

  /**
   *Setter for the change due including pending payments
   *
   * @param {*} pendingChange The new change due including pending payments
   */
  set pendingChange(pendingChange) {
    this._pendingChange = new Decimal(pendingChange); // safe decimal precision
    this.trigger(_Events.CHANGE_DUE_CHANGED, this._pendingChange);
  }

  /**
   * Getter for the total amount of cash payments
   *
   * @returns {*}
   */
  get cashPayment() {
    return this._cashPayment;
  }

  /**
   * Setter for the total amount of cash payments
   *
   * @param {*} total The new cash payments total
   */
  set cashPayment(total) {
    this._cashPayment = new Decimal(total);
    this.trigger(_Events.CASH_CHANGED, this._cashPayment);
  }

  /**
   * Getter for the managed sale's current total
   *
   * @returns {Decimal}
   */
  get saleTotal() {
    let saleTotal = new Decimal(0);
    if (this.sale.has('displayableTotal')) {
      // BigNumber.js will no longer throw an exception when not getting a number
      // or a Decimal. The following situation would return a Decimal with {c:null, e:null, s:0}
      saleTotal = new Decimal(this.sale.get('displayableTotal'));

      // Still in case our API does it, this would have a dramatic effect on our code.
      // Hence, we throw an unhandled exception to be caught by the monitoring
      // if the contract ever change.
      if (saleTotal.isNaN()) {
        throw new Error(`The "displayableTotal" return by the API is not a number or decimal.`);
      }
    }

    return saleTotal;
  }

  /**
   * Getter for the currently available credit balance, if any, for this sale.
   * @returns {Decimal|null} - The decimal available balance, or null.
   */
  get availableCreditBalance() {
    let balance = null;
    if (this.customer) {
      let account = this.customer.getRelation('CreditAccount', CreditAccount);
      if (account) {
        balance = account.availableCreditBalance;
      }
    }
    return balance;
  }

  /**
   * Getter for the credit balance for the customer.  The credit balance would be
   * the value of the credit that the customer has on deposit and in store credit.
   *
   * @returns {Decimal}   The amount of in-store credit and deposits the customer
   *                      has on hand to make payments (does NOT include the customer's
   *                      credit limit).
   */
  get creditAccountBalance() {
    let balance = new Decimal(0);
    if (this.customer) {
      let account = this.customer.getRelation('CreditAccount', CreditAccount);
      if (account) {
        // Any available credit balance is expressed as a negative value in the API.
        // A positive credit balance indicates that the customer owes the store money.
        balance = account.get('balance').negated();
      }
    }
    return balance;
  }

  /**
   * Getter for the creditAccountID associated with this sale, if any.
   *
   * @returns {Number|null} - The credit account id.  Or null.
   */
  get creditAccountID() {
    let creditAccountID = null;
    if (this.customer && this.customer.get('creditAccountID') > 0) {
      creditAccountID = this.customer.get('creditAccountID');
    }
    return creditAccountID;
  }

  /**
   * Getter for retrieving credit/debit payments from pending payments and the sale.
   *
   * @returns {SalePaymentCollection} A collection of credit/debit sale payments.
   */
  get creditDebitPayments() {
    let creditDebitPayments = this.getPaymentsOfType(PaymentType.Types.CREDIT_CARD);

    if (this._servicePayments) {
      creditDebitPayments = new SalePaymentCollection(
        creditDebitPayments.filter(salePayment => {
          let paymentServicePayment = this._servicePayments.findWhere({
            paymentID: salePayment.get('paymentID'),
          });

          return paymentServicePayment
            ? !salePayment.get('archived') &&
                paymentServicePayment.get('status') === PaymentModel.Status.APPROVED &&
                paymentServicePayment.get('operationType') !== PaymentModel.OperationType.UNKNOWN &&
                !this.isRefundOfPaymentInSale(paymentServicePayment)
            : true;
        })
      );

      // Add the payment service transaction details to the returned payments.  This is
      // for the listing view so like for pending payments this will be deprecated when/if
      // we use a custom listing view to list the payments.
      creditDebitPayments.forEach(salePayment => {
        let paymentServicePayment = this._servicePayments.findWhere({
          paymentID: salePayment.get('paymentID'),
        });

        if (paymentServicePayment) {
          // create a payment to obtain the payment details
          const payment = PaymentFactory.createPaymentFromModel(salePayment, paymentServicePayment);

          salePayment.details = payment.details;
          salePayment.details.operationType = paymentServicePayment.get('operationType');
        } else if (salePayment.has('CCCharge')) {
          let payment = PaymentFactory.createPaymentFromModel(salePayment);
          salePayment.details = payment.details;
        }
      });
    }

    return creditDebitPayments;
  }

  /**
   * Returns the total amount of credit payments currently in the sale.
   *
   * @returns {Decimal}   The total amount of credit payments in the sale.
   */
  get totalCreditPayments() {
    let creditPaymentAmount = new Decimal(0);
    const creditPayments = this.creditDebitPayments;

    creditPayments.forEach(payment => {
      creditPaymentAmount = creditPaymentAmount.plus(payment.get('amount'));
    });

    return creditPaymentAmount;
  }

  /**
   * Returns tipEnabled from sale.
   * @returns {Boolean} TipEnabled from sale
   */
  get isTipEnabled() {
    if (this.sale.has('tipEnabled')) {
      return this.sale.get('tipEnabled');
    }
    return false;
  }

  /**
   * Retrieves a service payment from the sale manager
   *
   * @param {string} paymentID    The payment ID
   * @returns {*|Backbone.Model}
   */
  getServicePayment(paymentID) {
    return this._servicePayments.findWhere({
      paymentID,
    });
  }

  /**
   * Retreives a service payment containing a transaction with the specified gateway reference.
   *
   * @param {string} gatewayReference     The reference number used by the gateway to identify a
   *                                      transaction
   * @returns {Payment|null}
   */
  getServicePaymentByGatewayRef(gatewayReference) {
    let servicePayment = null;
    this._servicePayments.forEach(payment => {
      if (payment.paymentTransaction.get('gatewayRecordRef') === gatewayReference) {
        servicePayment = payment;
      }
    });

    return servicePayment;
  }

  /**
   * Getter for retrieving gift card payments from pending payments and the sale.
   *
   * @returns {SalePaymentCollection} A collection of gift card sale payments.
   */
  get giftCardPayments() {
    let giftCardPayments = this.getPaymentsOfType(PaymentType.Types.GIFT_CARD);

    // When we create a gift card a payment will be created in the sale attached to
    // a sale account.  Just ignore it since we don't want to account for it in the
    // sale.
    giftCardPayments = new SalePaymentCollection(
      giftCardPayments.filter(salePayment => !salePayment.has('SaleAccounts'))
    );

    return giftCardPayments;
  }

  /**
   * Retrieves the total of unarchived payments already attached to the sale.  It also
   * excludes any special payments added to the sale for the specific case where there
   * is a gift card purchase in the sale.
   *
   * @returns {Decimal}   The total contribution of payments currently in the sale
   */
  get totalInSalePayments() {
    let totalPayments = new Decimal('0.0');

    let salePayments =
      this.sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

    salePayments.forEach(salePayment => {
      if (!salePayment.get('archived') && !salePayment.has('SaleAccounts')) {
        let servicePayment = this._servicePayments.findWhere({
          paymentID: salePayment.get('paymentID'),
        });

        if (servicePayment) {
          // if there's a service payment we need to make sure it's approved and not refunded
          if (
            servicePayment.get('status') === PaymentModel.Status.APPROVED &&
            servicePayment.get('operationType') !== PaymentModel.OperationType.UNKNOWN &&
            !this.isRefundOfPaymentInSale(servicePayment)
          ) {
            totalPayments = totalPayments.plus(salePayment.get('amount'));
          }
        } else {
          totalPayments = totalPayments.plus(salePayment.get('amount'));
        }
      }
    });

    return totalPayments;
  }

  /**
   * Sets the refundable payments associated with a sale.
   *
   * @param payments {PaymentList}  The payments that may be refunded
   */
  set refundablePayments(payments) {
    if (payments instanceof PaymentList) {
      this._refundablePayments = payments;
    }
  }

  /**
   * Retrieves the refundable payments associated with a sale.
   *
   * @returns {PaymentList}   The payments that may be refunded
   */
  get refundablePayments() {
    return this._refundablePayments || new PaymentList();
  }

  /**
   * Retrieves a list of Payments that may be refunded.  The refundable payments are populated
   * when the SaleManager is created (via a static factory method) or when the sale is refreshed
   * from the SaleManager (refreshSale).
   *
   * @returns {PaymentList}     A list of refundable payments
   */
  get refundablePaymentList() {
    let refundablePayments = new PaymentList();

    if (this.refundablePayments) {
      refundablePayments = this.refundablePayments.filter(
        payment => !payment.model.get('archived')
      );
    }

    return refundablePayments;
  }

  /**
   * Retrieves a list of Pending Payments on the refund.
   *
   * @returns {Array.Payment}     An array of pending payments
   */
  get pendingPayments() {
    return this._pendingPayments;
  }

  /**
   * Syncs the sale manager model with the API
   *
   * @returns {*}
   */
  refreshSale() {
    let deferred = $.Deferred();
    window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHING));

    if (this.sale) {
      let filter = new Filter().loadRelations('all');
      let paymentFilter = new Filter().exactly('saleID', this.sale.id);

      // Data like sale lines can either be present or not
      // which causes issues with how Backbone likes to operate.
      // To prevent stale data from being PUT to the server make
      // sure we clean the model before refreshing it (but don't
      // notify any listeners)

      // need to preserve the id because it is cleared as well
      let id = this.sale.get(this.sale.idAttribute);
      this.sale.clear({ silent: true });
      this.sale.set(this.sale.idAttribute, id);

      $.when(this.sale.fetch(filter), this._servicePayments.fetch(paymentFilter))
        .done(() => {
          let customerDeferred = $.Deferred();
          let customer = null;
          if (this.sale.get('customerID')) {
            customer = new Customer({ customerID: this.sale.get('customerID') });
            customerDeferred = customer.fetch(filter);
          } else {
            customerDeferred.resolve();
          }

          $.when(customerDeferred)
            .done(() => {
              this._attachedCustomer = customer;

              if (this.saleTotal.isZero() && !this.deposits.hasDeposits) {
                // Any existing pending payments should be discarded
                // for missing a zero total sale
                this._pendingPayments = {};
              }

              this.deposits.setInSaleDepositRefunds(this.sale);

              this._calculatePayments();
              SaleManager.fetchRefundablePayments(this.sale)
                .done(refundablePayments => {
                  this.refundablePayments = refundablePayments;
                  this.trigger(_Events.SALE_REFRESHED);
                  window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHED));

                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              window.console.error('There was an error while attempting to retrieve the sale.');
              deferred.reject();
            });
        })
        .fail(() => {
          window.console.error('There was an error while attempting to retrieve the sale.');
          deferred.reject();
        });
    } else {
      window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHED));
      deferred.resolve();
    }

    return deferred.promise();
  }

  /**
   * Finishes a sale.  Any pending payments are added to the sale before finishing the
   * sale.  This is a convenience method for updateSale({ completed: true }).
   *
   * @returns {jQuery.Promise}    A jQuery promise that resolves once the sale has been
   *                              successfully finished.
   */
  finishSale() {
    this.trigger(_Events.COMPLETE_SALE_STARTED);
    return this.updateSale({
      complete: true,
    });
  }

  /**
   * Updates the API sale data with the latest sale data including any pending payments or deposits.  If
   * the completed option is set to true, then the sale will be validated before any update is attempted
   * with the server.
   *
   * @param {Object} options  Options used to configure the update request
   * @param {string} options.complete     When true, the sale data will be validated before the request is
   *                                      made and, if valid, will attempt to complete the sale with the API.
   * @returns {*}
   */
  updateSale(options = {}) {
    let sale = new Sale();
    let deferred = $.Deferred();

    let status = this._canFinishSale();

    // We always allow the sale update to happen if we're not trying to complete the sale even
    // if validation fails.  The reason for this is to allow for manual credit card payments
    // where we'll have an out of balance sale until the credit card payment is taken.
    if (status.error === _Errors.NO_ERROR || !options.complete) {
      // create a clone of the managed sale
      sale.set('saleID', this._sale.get('saleID'));
      sale.set(_.clone(this._sale.attributes));

      // indicate to the API that the sale should be finished
      sale.set('completed', options.complete);

      // Remove the sale lines from the sale if we're about to complete it.
      // Lets the backend calculate totals to remove discrepancies caused
      // by Price Rules / Discounts / etc. See CLD-13170.
      if (options.complete) {
        sale.unset('SaleLines');
      }

      // ignore any pending payments when there's nothing to pay or refund
      // add pending payments to the sale
      let salePayments =
        sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

      _.each(this._pendingPayments, payment => {
        // Add pending payments to the sale when the sale total is not zero, we have deposits
        // or when we're not trying to complete the sale.   The last case lets us blindly add
        // payments to the sale without worrying about validation since the validation in the API
        // only occurs when we try to complete the sale.  This is necessary to allow for manual
        // credit card payments where we'll be in a situation where the sale needs to be out of
        // balance until the manual credit card payment balances it.
        if (
          !this.saleTotal.isZero() ||
          this.deposits.hasDeposits ||
          options.complete === false ||
          payment instanceof CashPayment
        ) {
          // make sure the payment is assigned to the sale
          payment.model.set('saleID', sale.get('saleID'));

          // refunded payments might have a CCCharge attached; make sure we clear
          // it otherwise the API will complain
          if (payment.model.has('refPaymentID')) {
            payment.model.unset('CCCharge');
          }

          salePayments.add(payment.model);
        }
      });

      // add any deposit refunds to the sale
      _.each(this.deposits.depositRefunds, deposit => {
        // For deposit refunds, the payment that we set to the sale is distinguished only by
        // the fact that it's associated with a credit account ID.  When the sale is completed,
        // the API will notice that the payment has an assigned credit account ID and will thus
        // treat the 'payment' as a deposit refund.  Deposit refunds for payment service-backed
        // payments are treated differently because they must be committed immediately rather
        // than at sale completion time.
        salePayments.add(deposit.model);
        salePayments.add(deposit.depositPayment.model);
      });

      if (salePayments.length) {
        // Each SalePayment object in the collection has a `paymentTypeID` and the linked PaymentType object.
        // Sending the PaymentType object could cause the Retail API to try to update the SalePayment object.
        // We don't want that to happen when updating the sale, so we remove the linked PaymentType, and only
        // keep the `paymentTypeID`.
        salePayments.forEach(salePayment => {
          salePayment.unset('PaymentType');
        });

        sale.set('SalePayments', {
          SalePayment: salePayments.toJSON(),
        });
      }

      this._updateSaleOnServer(sale)
        .done(() => {
          deferred.resolve();
        })
        .fail(saleManagerStatus => {
          deferred.reject(saleManagerStatus);
        });
    } else {
      this.trigger(_Events.COMPLETE_SALE_FAILED);

      if (status.error === _Errors.INTERNAL) {
        window.console.error(status.msg);
      }

      deferred.reject(status);
    }

    return deferred.promise();
  }

  /**
   * Commits an individual payment to the server immediately.
   *
   * @param {Payment} payment     The payment to save to the server.
   * @returns {jQuery.Promise}
   */
  commitPaymentToServer(payment) {
    let sale = new Sale();
    let salePayments = new SalePaymentCollection();

    sale.clear();
    sale.set('saleID', this._sale.get('saleID'));
    salePayments.add(payment.model);
    if (salePayments.length) {
      sale.set('SalePayments', {
        SalePayment: salePayments.toJSON(),
      });
    }

    return this._updateSaleOnServer(sale);
  }

  /**
   * Helper method to sync the SaleManager sale data with the server.
   *
   * @param {Sale} sale   The sale model
   * @private
   */
  _updateSaleOnServer(sale) {
    let deferred = $.Deferred();
    let filter = new Filter().loadRelations('all');

    // finish the sale
    sale
      .save(filter)
      .done(() => {
        // once the sale has been completed update the managed sale and clear the pending
        // payments
        this._sale = sale;
        this._pendingPayments = {};

        // ...and show a signature capture for Retail Manager if necessary
        RetailManager.sendSignatureData(this.creditDebitPayments);

        this.deposits.setInSaleDepositRefunds(this.sale);

        this._calculatePayments();
        deferred.resolve();
      })
      .fail(response => {
        if (
          typeof response.responseJSON.errorClass !== 'undefined' &&
          response.responseJSON.errorClass === 'SwipeError'
        ) {
          // If a bad swipe was the cause of the error, remove it
          // from the list of pending payments
          let swipePayment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.SWIPE,
            0,
            '',
            response.responseJSON.remoteReference
          );

          this.removePayment(swipePayment);
          this.removePayment(this.deposits.getDepositForPayment(swipePayment));
          this.deposits.removeDepositForPayment(swipePayment);

          this._calculatePayments();
          this.trigger(_Events.COMPLETE_SALE_FAILED);
        }

        // If we had a failure it could be because we've become out of sync with the server so
        // refresh the sale data and payments with the server.
        this.refreshSale().always(() => {
          deferred.reject(
            new SaleManagerStatus(_Errors.USER_FACING, response.responseJSON.message)
          );
        });
      });

    return deferred.promise();
  }

  /**
   * Adds a deposit to the sale.  This only applies the credit to the sale (which is not
   * committed until sale completion time).  Payments used to cover the deposit must
   * be handled by separate actions on the part of the user.
   *
   * @param {DepositPayment} deposit  The deposit payment
   * @param {isAccountPayment} isAccountPayment   true if the deposit is for an account payment
   * @returns {Promise}   Resolves once the payment has been added
   */
  addDeposit(deposit, isAccountPayment = false) {
    let deferred = $.Deferred();
    if (deposit instanceof DepositPayment) {
      // The deposit total grabs the absolute total, while
      // the creditBalance is the sum of all credit payments (which is negative).
      // They both need the same sign.
      const creditBalance = this.getTotalPaymentsOfType(deposit.paymentTypeID).abs();

      if (!creditBalance.isEqualTo(this.deposits.depositTotal)) {
        // our in-sale credit account payments differ from our depositTotal.  This can happen
        // if a credit account payment was added before a deposit was added.  Just sync the
        // current amount to the deposit amount to clear out the prior amount.
        let synchronizingPayment = deposit.clone();
        synchronizingPayment.amount = new Decimal(this.deposits.depositTotal);
        this.addPayment(synchronizingPayment);
      }

      if (this.deposits.addDeposit(deposit, isAccountPayment)) {
        // we clone the deposit so that side-effects don't affect the data
        // we're tracking in the deposit
        this.addPayment(deposit.clone(), { merge: true })
          .done(() => {
            this._calculatePayments();
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        deferred.reject();
      }
    } else {
      window.console.error('Payment was not a deposit.');
      deferred.reject();
    }

    return deferred;
  }

  /**
   * Removes all deposits from the sale.  This only removes the credit account amount
   * from the sale in order to conform to the legacy webPOS methodology.
   */
  removeDeposits() {
    if (this.deposits.hasDeposits) {
      // We're mimicking the legacy UI here so we only remove
      // the deposit itself and the not the payment for the deposit.
      _.each(this.deposits.deposits, deposit => {
        this.removePayment(deposit);
      });

      this.deposits.clearDeposits();

      this._calculatePayments();

      this.trigger(_Events.DEPOSITS_CHANGED);
    }
  }

  /**
   * Adds a payment to the sale.  Pending payments are added to the pending payment list.
   *
   *
   * @param payment
   * @param {Object} options  Options to apply when adding the payment
   * @param {boolean} options.merge - Whether to merge (sum) a pending payment
   *   that does not allow multiples (cash, credit account, etc)  or replace it with
   *   the provided payment amount. Defaults to false.
   * @param {boolean} options.ignoreValidationErrors    When true, the payment will be added without
   *   validation (necessary to handle Manual Credit Card payments)
   *
   * @returns {jQuery.Promise}    Resolves once payment has been added
   */
  addPayment(payment, options = {}) {
    let deferred = $.Deferred();

    if (
      payment &&
      (this.validatePayment(payment).error === SaleManager.Errors.NO_ERROR ||
        options.ignoreValidationErrors)
    ) {
      if (payment.commitImmediately && !payment.amount.isZero()) {
        payment
          .commit()
          .done(() => {
            // refresh the sale to obtain the new payment and total
            this.refreshSale()
              .done(() => {
                deferred.resolve(payment.model.id);
              })
              .fail(() => {
                deferred.reject();
              });
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        let pendingPayment = payment;
        if (!payment.allowMultiple) {
          // for payments that disallow multiple payments of the same type, just replace
          // the current pending payment
          let existingPendingPayment = _.find(
            this._pendingPayments,
            currentPayment => currentPayment.paymentTypeID === payment.paymentTypeID
          );

          if (existingPendingPayment) {
            if (options.merge) {
              existingPendingPayment.amount = existingPendingPayment.amount.plus(payment.amount);
            } else {
              existingPendingPayment.amount = payment.amount;
            }
            pendingPayment = existingPendingPayment;
          }
        }

        if (payment instanceof GiftCardPayment) {
          // Gift card payments only allow multiples for different gift cards.  If the same
          // gift card is added twice, it replaces the old payment like is the case for
          // payments that disallow multiple payments.
          let identicalGiftCardPayment = _.find(
            this._pendingPayments,
            currentPayment =>
              currentPayment.paymentTypeID === payment.paymentTypeID &&
              currentPayment.model.get('creditAccountID') === payment.model.get('creditAccountID')
          );

          if (identicalGiftCardPayment) {
            identicalGiftCardPayment.amount = identicalGiftCardPayment.amount.plus(payment.amount);
            pendingPayment = identicalGiftCardPayment;
          }
        }

        if (pendingPayment.amount.isZero()) {
          // we don't hold onto empty pending payments
          this.removePayment(pendingPayment);
        } else {
          // store the payment referenced by the model's internal ID
          this._pendingPayments[pendingPayment.model.get('remoteReference')] = pendingPayment;
        }

        this._calculatePayments();

        deferred.resolve(pendingPayment.model.get('remoteReference'));
      }
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Removes a payment from the sale.  Pending payments are removed immediately whereas
   * existing payments (i.e. registered with the server) are removed via the API.
   *
   * @param {Payment} payment    The payment to remove
   * @returns {jQuery.Promise}   Resolves when payment has been removed
   */
  removePayment(payment) {
    let deferred = $.Deferred();
    // check for the payment in the pending IDs first
    if (!payment) {
      deferred.reject(`The payment is empty.`);
    } else {
      if (this._pendingPayments[payment.model.get('remoteReference')]) {
        delete this._pendingPayments[payment.model.get('remoteReference')];
        this._calculatePayments();
        this.refreshSale()
          .done(() => {
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else if (payment.isServicePayment()) {
        if (payment.serviceModel) {
          payment.serviceModel
            .refundPayment({ refundTip: payment.refundTip })
            .done(() => {
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              deferred.reject();
            });
        } else {
          // no matching service payment was found
          deferred.reject();
        }
      } else {
        // the ID doesn't appear to be a pending payment so
        // let's see if it's a sale payment ID in the existing sale
        let salePayments = this._sale.getRelation('SalePayments', SalePaymentCollection);
        let salePayment = salePayments
          ? salePayments.findWhere({ salePaymentID: payment.model.id })
          : null;

        // The API only supports removing (i.e. archiving) payments through the sale currently
        if (salePayment) {
          salePayment.set('archived', true);
          salePayments.add(salePayment, { merge: true });
          this._setSalePayments(salePayments);
          this._sale
            .save(true)
            .done(() => {
              this._calculatePayments();
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(errorMessage => {
              deferred.reject(errorMessage);
            });
        } else {
          deferred.reject(`The salePayment is empty.`);
        }
      }
    }

    return deferred;
  }

  /**
   * Updates a pending payment in the sale.
   *
   * @param {*} payment A payment object
   */
  updatePaymentAmount(payment) {
    // currently only support updating payment amounts where multiples are not allowed;
    // this will likely change once V2 payments service is ready
    if (_.isObject(payment) && !payment.allowMultiple) {
      let existingPendingPayment = _.find(
        this._pendingPayments,
        currentPayment => currentPayment.paymentTypeID === payment.paymentTypeID
      );

      if (existingPendingPayment) {
        if (payment.amount.isZero()) {
          // remove the pending payment if the amount is zero.  This is necessary
          // so we don't accidentally send a zero payment when completing a sale
          this.removePayment(existingPendingPayment);
        } else {
          existingPendingPayment.amount = payment.amount;

          this._pendingPayments[
            existingPendingPayment.model.get('remoteReference')
          ] = existingPendingPayment;
        }

        this._calculatePayments();
      }
    } else {
      window.console.error('A payment must be specified to update.');
    }
  }

  /**
   * Voids a payment using a terminal.  The terminal is invoked to perform any terminal actions
   * required to the void the payment (e.g. in cases where there is no API support for a particular
   * terminal).  Once that operation is complete, the payment is removed from the sale manager.
   *
   * @param {Terminal} terminal   The terminal to void the transaction
   * @param {Payment} payment     The payment to be voided
   * @returns {*}
   */
  voidTerminalPayment(payment, terminal) {
    let deferred = $.Deferred();

    if (terminal && payment) {
      terminal
        .voidTransaction(payment)
        .done(() => {
          // the terminal should have archived the payment so sync
          // the sale manager
          deferred = this.removePayment(payment);
        })
        .fail(response => {
          window.console.error('Failed to void the transaction:', response);
          deferred.reject();
        });
    } else {
      window.console.error('Cannot void payment:', payment, terminal);
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Determines if there is an existing pending payment of the type of payment specified.
   *
   * @param {*} payment   The payment of the type to be matched to any existing payments
   * @returns {boolean}
   */
  hasExistingPaymentOfType(payment) {
    let totalPaymentsOfType = this.getTotalPaymentsOfType(payment.paymentTypeID);

    return !totalPaymentsOfType.isZero();
  }

  /**
   * Reports if there are pending payments currently in the sale.
   *
   * @return {boolean} True if there are pending payments in the sale
   */
  hasPendingPayments() {
    return this._pendingPayments && !_.isEmpty(this._pendingPayments);
  }

  /**
   * Returns the total amount of payments of the type specified that exist in the sale.  Only accounts
   * for pending payments.
   *
   * @param {number|string} paymentTypeID The payment type ID of the pending payment to retrieve
   * @returns {Decimal}   The total amount of pending payments of the type specified
   */
  getTotalPaymentsOfType(paymentTypeID) {
    let total = new Decimal(0);

    _.each(this._pendingPayments, currentPayment => {
      if (currentPayment.paymentTypeID == paymentTypeID) {
        total = total.plus(currentPayment.amount);
      }
    });

    return total;
  }

  /**
   * Determines if the specified payment may be added to the sale.
   *
   * @returns {SaleManagerStatus} The status of the operation.  Returns a status
   *                              with NO_ERROR if payments can be accepted.
   */
  validatePayment(payment) {
    let status = new SaleManagerStatus();

    if (!this.sale.has('displayableTotal')) {
      status = new SaleManagerStatus(_Errors.INTERNAL, 'Invalid sale received from API.');
    } else if (
      this.saleTotal.isZero() &&
      !this.deposits.hasDeposits &&
      !(payment instanceof DepositPayment) &&
      payment &&
      !payment.amount.isZero() // we'll allow clearing payments
    ) {
      let amount = Format.formatMoney(this.saleTotal);
      status = new SaleManagerStatus(
        _Errors.USER_FACING,
        `You can not enter a payment because the sale total is <var>${amount}</var>.`
      );
    } else if (
      payment &&
      !(payment instanceof CashPayment) &&
      !(payment instanceof DepositPayment)
    ) {
      try {
        let currentPaymentOfTypeThatDisallowsMultiples = new Decimal(0);
        if (!payment.allowMultiple) {
          // Payments that disallow multiple payments of the same type per sale must be
          // excluded when we're determining if we can add the payment to the sale because
          // the payment we're checking will replace the old when when we add it to the sale.
          currentPaymentOfTypeThatDisallowsMultiples = this.getTotalPaymentsOfType(
            payment.paymentTypeID
          );
        }

        let currentPaymentForSameGiftCard = new Decimal(0);
        if (payment instanceof GiftCardPayment) {
          // As with most things, gift cards are special in that we allow multiple payments
          // to different cards but only one payment for each card within a specific sale.  So,
          // when determining if we can add the new gift card payment to the sale we need to
          // exclude any prior payment that we added to the sale.
          let identicalGiftCardPayment = _.find(
            this._pendingPayments,
            currentPayment =>
              currentPayment.model.get('creditAccountID') === payment.model.get('creditAccountID')
          );

          if (identicalGiftCardPayment) {
            currentPaymentForSameGiftCard = identicalGiftCardPayment.amount;
          }
        }

        if (this.isRefundSale()) {
          if (
            payment.amount
              .plus(this.pendingPaymentsTotal)
              .minus(currentPaymentOfTypeThatDisallowsMultiples)
              .minus(currentPaymentForSameGiftCard)
              .minus(this.deposits.depositTotal)
              .minus(payment.tipAmount || 0)
              .minus(this.calcTips || 0)
              .isLessThan(this.saleTotal)
          ) {
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a refund greater than the total.'
            );
          }
        } else if (
          payment.amount
            .plus(this.pendingPaymentsTotal)
            .minus(currentPaymentOfTypeThatDisallowsMultiples)
            .minus(currentPaymentForSameGiftCard)
            .minus(this.cashPayment)
            .minus(this.deposits.depositTotal)
            .minus(payment.tipAmount || 0)
            .minus(this.calcTips || 0)
            .isGreaterThan(this.saleTotal)
        ) {
          // we have too many payments (other than cash payments)
          status = new SaleManagerStatus(
            _Errors.USER_FACING,
            'You can not enter a payment greater than the sale total.'
          );
        }
      } catch (err) {
        status = new SaleManagerStatus(
          _Errors.INTERNAL,
          'Invalid Amount Entered, UI amount will be reset to the previous valid amount'
        );
      }
    }

    return status;
  }

  /**
   * Determines if the managed sale is a sale refund.  Currently this is solely determined
   * by whether the sale total is a negative amount.
   *
   * @returns {*}
   */
  isRefundSale() {
    let isRefundSale;

    try {
      let saleTotal = new Decimal(this.saleTotal);
      isRefundSale = saleTotal.isNegative();
    } catch (error) {
      isRefundSale = false;
    }

    return isRefundSale;
  }

  /**
   * Coverts the sale manager data into an object.  Useful for passing data into
   * a template for use rather than specifying individual properties.  The name of
   * the method is chosen to be compatible with JSON.stringify.
   */
  toJSON() {
    return {
      sale: this._sale ? this._sale.toJSON() : new Sale().toJSON(),
      saleTotal: this.saleTotal,
      totalPayments: this.pendingPaymentsTotal,
      balance: this.pendingBalance,
      change: this.pendingChange,
      cashPayment: this.cashPayment,
      isRefundSale: this.isRefundSale(),
    };
  }

  /**
   * Determines if the sale has any pending credit card payments.  This is primarily for
   * iZettle payments which have no back-end integration with the server and is only supported
   * with Retail Manager.
   *
   * @returns {boolean}   True if there are uncommitted payments with the server.
   */
  hasUnsavedCreditPayments() {
    let unsavedPayments = _.find(
      this._pendingPayments,
      payment => payment._model.get('PaymentType').type === PaymentType.Types.CREDIT_CARD
    );

    return !!unsavedPayments;
  }

  addServiceDepositRefundToSale(depositRefund) {
    let deferred = $.Deferred();
    if (depositRefund.depositPayment.isServicePayment()) {
      // set the credit account payment on the sale
      this.commitPaymentToServer(depositRefund)
        .done(() => {
          this.deposits
            .refund(depositRefund)
            .done(() => {
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              // the deposit payment failed so archive the balancing credit account payment
              depositRefund.model.set('archived', true);
              this.commitPaymentToServer(depositRefund);

              deferred.reject();
            });
        })
        .fail(() => {
          deferred.reject();
        });
    }

    return deferred.promise();
  }

  /**
   * Updates the SalePayments relation in the Sale model with the attributes from a
   * SalePaymentCollection.
   *
   * @param {SalePaymentCollection} salePayments  The sale payments to be written to the Sale model
   * @private
   */
  _setSalePayments(salePayments) {
    // need to conform to the wacky API representation
    this._sale.set('SalePayments', {
      SalePayment: salePayments.toJSON(),
    });
  }

  /**
   * Calculates the total, balance and change due for any existing pending payments.  The
   * calculated values are exposed as pending payment properties on the SaleManager.
   *
   * @private
   */
  _calculatePayments() {
    try {
      let totalInSalePayments = this.totalInSalePayments;
      let change = new Decimal(0);
      let balance = new Decimal(0);
      let totalCashPayments = new Decimal(0);
      let saleTotal = new Decimal(this.saleTotal);
      let calcTips = this.sale.get('calcTips') || new Decimal(0);

      // Calculate in pendingPayments as part of the totalInSalePayments
      if (!_.isEmpty(this._pendingPayments)) {
        // Finding all the payments that are non (NOT) CashPayment or DepositPayments
        let nonCashOrDepositPayments = _.reject(
          this._pendingPayments,
          payment => payment instanceof CashPayment || payment instanceof DepositPayment
        );
        // Finding all the payments that are cash payments, normally there should be only one instance
        let cashPayments = _.filter(
          this._pendingPayments,
          payment => payment instanceof CashPayment
        );

        // Adding non-cash and deposit pending payments to the totalInSalePayments
        _.each(nonCashOrDepositPayments, payment => {
          payment.amount = payment.amount.toFixed(Localize.getCurrencyPrecision());
          totalInSalePayments = totalInSalePayments.plus(payment.amount);
          calcTips = calcTips.plus(payment.tipAmount || 0);
        });

        let owesMoneyToCustomer = saleTotal.minus(totalInSalePayments).isLessThan(0);

        // Adding the cash payments to the totalInSalePayments
        _.each(cashPayments, payment => {
          payment.amount = payment.amount.toFixed(Localize.getCurrencyPrecision());
          totalCashPayments = totalCashPayments.plus(payment.amount);
        });

        totalInSalePayments = totalInSalePayments.plus(totalCashPayments);

        // Calculating the change
        if (!owesMoneyToCustomer) {
          // the calculated totalInSalePayments includes deposits so make sure we take that into account
          // when calculating the change due
          const saleTotalWithDeposits = saleTotal.plus(this.deposits.depositTotal);
          if (totalInSalePayments.isGreaterThan(saleTotalWithDeposits)) {
            change = totalInSalePayments.minus(saleTotalWithDeposits).minus(calcTips);
          }
        }
      }

      // The balance is negative number because the totalInSalePayments is higher than the saleTotal,
      // so forth we need to exclude the depositTotal and the calcTips by adding them to the balance.
      balance = saleTotal
        .minus(totalInSalePayments.minus(change))
        .plus(this.deposits.depositTotal)
        .plus(calcTips);
      // We make sure to update the corresponding attributes in the SaleManger with the right value
      // from the sale
      this.pendingPaymentsTotal = totalInSalePayments;
      this.pendingBalance = balance;
      this.pendingChange = change;
      this.cashPayment = totalCashPayments;
      this.calcTips = calcTips;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Determines if the sale can be finished.
   *
   * @returns {SaleManagerStatus} The status of the operation.  Returns a status
   *                              with NO_ERROR if the sale can be finished.
   * @private
   */
  _canFinishSale() {
    let status = new SaleManagerStatus();
    if (this._sale) {
      if (this.sale.has('displayableTotal')) {
        let saleTotal;
        try {
          saleTotal = new Decimal(this.sale.get('displayableTotal'));
        } catch (err) {
          // bad data came back from the API
          status = new SaleManagerStatus(_Errors.INTERNAL, 'Invalid sale received from API.');
          throw err;
        }

        const calcTips = this.calcTips;
        if (this.isRefundSale()) {
          if (this.pendingPaymentsTotal.isGreaterThan(saleTotal)) {
            // we don't have enough refunds to finish the sale
            status = new SaleManagerStatus(_Errors.USER_FACING, 'Refunds are insufficient.');
          }

          if (this.pendingPaymentsTotal.minus(calcTips).isLessThan(saleTotal)) {
            // we have too may refunds (other than cash payments)
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a refund greater than the total.'
            );
          }
        } else {
          if (
            this.pendingPaymentsTotal
              .minus(this.deposits.depositTotal)
              .minus(calcTips)
              .isLessThan(saleTotal) ||
            (this.deposits.hasDeposits &&
              this.pendingPaymentsTotal.isLessThan(this.deposits.depositTotal))
          ) {
            // we don't have enough payments to finish the sale
            status = new SaleManagerStatus(_Errors.USER_FACING, 'Payments are insufficient.');
          }

          if (
            this.pendingPaymentsTotal
              .minus(this.cashPayment)
              .minus(this.deposits.depositTotal)
              .minus(calcTips)
              .isGreaterThan(saleTotal)
          ) {
            // we have too many payments (other than cash payments)
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a payment greater than the sale total.'
            );
          }
        }
      } else {
        status = new SaleManagerStatus(_Errors.INTERNAL, 'The sale has no total.');
      }
    } else {
      status = new SaleManagerStatus(_Errors.INTERNAL, 'There is no sale to finish.');
    }

    return status;
  }

  /**
   * Event handler for when the Sale's total changes.
   *
   * @private
   */
  _onTotalChanged() {
    this._calculatePayments();
  }

  /**
   * Retrieves payment of the type specified from the sale and pending payments.
   *
   * @returns {SalePaymentCollection} A collection of sale payments.
   */
  getPaymentsOfType(paymentType) {
    const salePayments =
      this.sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

    _.each(this._pendingPayments, payment => {
      salePayments.add(payment.model);

      if (payment.details) {
        // Terminal payments may have details that very so normalize where we
        // story the data with the model by attaching it to the model directly
        // rather than a model attribute.  This is primarily used to display
        // the appropriate data in the listing view.  We can get rid of this
        // if/when we re-factor the payment listing to use a custom listing view
        // rather than the Marionette listing view.
        payment.model.details = payment.details;
      }

      if (payment.transactionReferenceID) {
        // iZettle payments have a reference ID.  Expose that on the model
        // so that we can reference it but it's NOT a model property.
        payment.model.transactionReferenceID = payment.transactionReferenceID;
      }
    });

    return new SalePaymentCollection(
      salePayments.filter(salePayment => {
        const paymentTypeRelation = salePayment.getRelation('PaymentType', PaymentType);
        const isValidPendingPayment =
          !salePayment.get('archived') && !salePayment.get('amount').isZero();

        if (isValidPendingPayment) {
          /**
           * If the Payment Collection is not coming from the Payment Service,
           * we fallback on the client-side version of the payment types.
           *
           * The supported type should be [CREDIT_CARD, GIFT_CARD, USER_DEFINED]
           */
          if (!this._isPaymentCollectionFromService) {
            return (
              paymentTypeRelation &&
              [
                PaymentType.Types.CREDIT_CARD, // This is type needs to be present for the legacy gateway that might still use it.
                PaymentType.Types.GIFT_CARD, // This is one of the default type that can't be archived that need to be present.
                PaymentType.Types.USER_DEFINED, // This is the type returned for all the custom payment types
              ].includes(paymentTypeRelation.get('type'))
            );
          }

          return paymentTypeRelation && paymentTypeRelation.get('type') === paymentType;
        }

        return false;
      })
    );
  }

  /**
   * Gets the list of Refundable Payment types on the current sale
   * and sets their minimum/maximum refund value
   *
   * @returns {Array} - An array of refundable payment types
   */
  getRefundablePaymentTypes() {
    // We add a fake "Refund As Any" payment to handle ad hoc refunds;
    // this needs to be recalculated if the sale total changes. However
    // it's pretty harmless to recalculate it whenever this function is called
    this.refundablePayments = this.refundablePayments.filter(
      refundablePayment => refundablePayment.model.get('PaymentType').paymentTypeID
    );

    // Deposits work by adding a negative charge to a non-refund sale.
    // We want to remove these charges from the list of Refundable Payments-
    // They're not refundable payments, they're a hack to get everything else
    // to behave
    this.refundablePayments = this.refundablePayments.filter(
      refundablePayment => !refundablePayment.amount.isNegative()
    );

    // Now we're all cleaned up!

    // In the case of an ad-hoc refund, we want to pretend there was
    // a Refund as Any payment whose amount is the difference between
    // the sum of the original payments and the current pending balance
    // (e.g. original payments were $15, refund total is -$17; create a
    // refund as any payment for $2)
    let sumOfOriginalPayments = new Decimal(0);
    this.refundablePayments.forEach(refundablePayment => {
      sumOfOriginalPayments = sumOfOriginalPayments.minus(refundablePayment.amount);
    });

    if (this.saleTotal.isLessThan(sumOfOriginalPayments)) {
      let difference = this.saleTotal.minus(sumOfOriginalPayments).abs();
      let blankPaymentType = new PaymentType({
        type: PaymentType.Types.USER_DEFINED,
      });

      let salePayment = new SalePayment({
        amount: difference,
        PaymentType: blankPaymentType.toJSON(),
      });

      this.refundablePayments = this.refundablePayments.add(
        PaymentFactory.createPaymentFromModel(salePayment)
      );
    }

    let refundablePaymentTypes = PaymentTypeManager.getRefundablePaymentTypes(
      this.refundablePayments
    );
    refundablePaymentTypes = refundablePaymentTypes.toJSON();
    refundablePaymentTypes = this._setMinimumPaymentPerRefundablePaymentType(
      refundablePaymentTypes
    );
    refundablePaymentTypes = this._setMaximumPaymentPerRefundablePaymentType(
      refundablePaymentTypes
    );

    return refundablePaymentTypes;
  }

  /**
   * Gets a particular refundable payment type after they've been initialized
   * with minimum/maximum refund values
   *
   * @param paymentTypeID - The ID of the payment type
   */
  getRefundablePaymentTypeByID(paymentTypeID) {
    return _.find(
      this.getRefundablePaymentTypes(),
      refundablePaymentType => refundablePaymentType.paymentTypeID === paymentTypeID
    );
  }

  /**
   * Determines if the the refunded payment is a refund a payment currently in the sale.
   *
   * @param {Payment} servicePayment  The refund service payment
   */
  isRefundOfPaymentInSale(servicePayment) {
    let isRefundPayment = false;
    if (servicePayment.get('operationType') === PaymentModel.OperationType.REFUND) {
      // if this is a refund for a payment in the same sale then ignore this payment
      isRefundPayment =
        this._servicePayments.findWhere({
          paymentID: servicePayment.get('refundedPaymentID'),
        }) instanceof PaymentModel;
    }

    return isRefundPayment;
  }

  /**
   * Sets the minimum amount of money we have to refund per
   * payment type, if any
   *
   * @param refundablePaymentTypes     The list of refundable Payment Types
   * @returns {Array} - The modified array of refundable payment types
   * @private
   */
  _setMinimumPaymentPerRefundablePaymentType(refundablePaymentTypes) {
    _.each(refundablePaymentTypes, refundablePaymentType => {
      // Get the sum of all payments that -need- to be refunded as this type,
      // as well as the sum of all payments that don't
      let refundAsThisType = new Decimal(0);
      let refundAsOtherType = new Decimal(0);
      this.refundablePayments.forEach(refundablePayment => {
        if (
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID ===
          refundablePaymentType.paymentTypeID
        ) {
          refundAsThisType = refundAsThisType.minus(refundablePayment.amount);
        } else {
          refundAsOtherType = refundAsOtherType.minus(refundablePayment.amount);
        }
      });

      // Check if the sum of all other types are sufficient to complete the sale
      // If it is, great! The minimum is 0. Otherwise, a minimum exists and must
      // be calculated for Payment Types that are not set to Refund As Any.
      refundablePaymentType.minimumRefund = new Decimal(0);
      if (refundAsOtherType.isGreaterThan(this.saleTotal)) {
        refundablePaymentType.minimumRefund = this.saleTotal.minus(refundAsOtherType);
      }
    });

    return refundablePaymentTypes;
  }

  /**
   * Sets the maximum amount of money we can refund per
   * payment type, if any
   *
   * @param refundablePaymentTypes     The list of refundable Payment Types
   * @returns {Array} - The modified array of refundable payment types
   * @private
   */
  _setMaximumPaymentPerRefundablePaymentType(refundablePaymentTypes) {
    _.each(refundablePaymentTypes, refundablePaymentType => {
      refundablePaymentType.maximumRefund = new Decimal(0);

      this.refundablePayments.forEach(refundablePayment => {
        if (
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID ===
            refundablePaymentType.paymentTypeID ||
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID === 0
        ) {
          refundablePaymentType.maximumRefund = refundablePaymentType.maximumRefund.plus(
            refundablePayment.amount
          );
        }
      });

      refundablePaymentType.maximumRefund = refundablePaymentType.maximumRefund.negated();

      if (
        !refundablePaymentType.maximumRefund.eq(0) &&
        refundablePaymentType.maximumRefund.isLessThan(this.saleTotal)
      ) {
        refundablePaymentType.maximumRefund = this.saleTotal;
      }
    });

    return refundablePaymentTypes;
  }
}
